import { BREAKPOINTS } from '@app/constants/breakpoints';
import { useIsScreenSize } from '@app/utils/hooks';
import { usePopover } from '@app/utils/hooks/usePopover';
import { isNull } from '@app/utils/is';
import { DatePickerNs } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-laag';

import { FocusTrap } from '../FocusTrap';
import { PopoverSheet } from '../Popover/PopoverSheet';
import { Skeleton } from '../Skeleton';
import { SkeletonLayout } from '../SkeletonLayout';

import {
  DatePickerCalendar,
  DatePickerClearButton,
  DatePickerControls,
  DatePickerDrawer,
  DatePickerFooter,
  DatePickerTrigger,
} from './components';
import { DatePickerNavigationContextProvider, DatePickerSelectionContextProvider, DatePickerSettingsContextProvider } from './context';

type DatePickerComponentProps<T extends DatePickerNs.Value> = Omit<
  DatePickerNs.Props<T>,
  'isPastDisabled' | 'min' | 'max' | 'getShouldShowDot'
>;

const DatePickerComponent = React.forwardRef(
  <T extends DatePickerNs.Value>(props: DatePickerComponentProps<T>, ref: React.RefObject<HTMLInputElement>) => {
    const [isCalendarVisible, setIsCalendarVisible] = React.useState(false);
    const isMobile = useIsScreenSize(BREAKPOINTS.SM);
    const triggerRef = React.useRef<HTMLInputElement>(null);

    const { t } = useTranslation(['dates']);
    const {
      dataTestId,
      disabled = false,
      invalid = false,
      isClearable = true,
      onChange,
      placeholder = isCalendarVisible ? t('dates:select_date_placeholder') : t('dates:select_date'),
      shouldShowToday = true,
      triggerFormatter,
      size,
      value,
      isLoading = false,
      onClose,
      describedById,
      titleMobile,
    } = props;

    const showCalendar = React.useCallback(() => {
      setIsCalendarVisible(true);
    }, []);

    const handleChange = (newValue: T, shouldCloseCalendar = false) => {
      onChange(newValue);

      if (shouldCloseCalendar) {
        setIsCalendarVisible(false);
      }
    };

    const handleClose = () => {
      onClose?.();
      setIsCalendarVisible(false);
      triggerRef.current?.focus();
    };

    const [startDate] = [value].flat();
    const shouldShowClearButton = isClearable && !isNull(startDate);

    const { layerProps, triggerProps, layerSide, renderLayer } = usePopover({
      escapeHandlerEnabled: isCalendarVisible,
      handleDisappear: handleClose,
      layerProps: {
        isOpen: isCalendarVisible && !isMobile,
        placement: 'bottom-start',
        possiblePlacements: ['bottom-start', 'top-start'],
        triggerOffset: 4,
      },
    });

    return (
      <DatePickerNavigationContextProvider defaultVisibleDate={startDate ?? null}>
        <DatePickerSelectionContextProvider value={value ?? null} onChange={handleChange}>
          <SkeletonLayout isLoading={isLoading} skeleton={<Skeleton type="input" />}>
            <div className="flex flex-col items-end gap-1">
              <span {...(!isMobile ? triggerProps : {})} className="w-full">
                <DatePickerTrigger
                  dataTestId={dataTestId}
                  disabled={disabled}
                  invalid={invalid}
                  placeholder={placeholder}
                  formatter={triggerFormatter}
                  onClick={showCalendar}
                  size={size}
                  describedById={describedById}
                  ref={mergeRefs(triggerRef, ref)}
                  isCalendarVisible={isCalendarVisible}
                  onVisibilityChange={setIsCalendarVisible}
                />
              </span>
              {shouldShowClearButton && <DatePickerClearButton variant="text" />}
            </div>
            {isMobile ?
              <DatePickerDrawer
                isVisible={isCalendarVisible}
                onVisibilityChange={setIsCalendarVisible}
                titleMobile={titleMobile}
                placeholder={placeholder}
              >
                <DatePickerControls className="border-t border-t-neutral-90 pt-2" />
                <DatePickerCalendar />
                <DatePickerFooter shouldShowClear={isClearable} shouldShowToday={shouldShowToday} />
              </DatePickerDrawer>
            : renderLayer(
                <PopoverSheet {...layerProps} isOpen={isCalendarVisible} layerSide={layerSide} className="w-[--filterBarWidth] p-3">
                  <FocusTrap shouldReturnFocus>
                    <DatePickerControls />
                    <DatePickerCalendar />
                    <DatePickerFooter shouldShowClear={isClearable} shouldShowToday={shouldShowToday} />
                  </FocusTrap>
                </PopoverSheet>,
              )
            }
          </SkeletonLayout>
        </DatePickerSelectionContextProvider>
      </DatePickerNavigationContextProvider>
    );
  },
);

export const DatePicker = React.forwardRef(
  <T extends DatePickerNs.Value>(props: DatePickerNs.Props<T>, ref: React.RefObject<HTMLInputElement>) => {
    const { max = null, min = null, isPastDisabled = true, getShouldShowDot, ...rest } = props;

    return (
      <DatePickerSettingsContextProvider isPastDisabled={isPastDisabled} max={max} min={min} getShouldShowDot={getShouldShowDot}>
        <DatePickerComponent {...rest} ref={ref} />
      </DatePickerSettingsContextProvider>
    );
  },
);
